import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button, Menu, Modal } from "@mantine/core";
import {
    IconTrash,
    IconEdit,
    IconChevronDown,
    IconChevronRight,
    IconFolder,
    IconFolderOpen,
    IconCube,
    IconPlus,
    IconBuildingStore,
    IconBus,
    IconCar,
    IconSailboat,
    IconPlane,
    IconHelicopter,
    IconBike,
    IconBuilding,
    IconBed,
    IconHome,
    IconCompass,
    IconLuggage,
    IconTools,
    IconArmchair,
    IconUser,
    IconUsers,
    IconUsersGroup,
    IconBabyCarriage,
    IconWheelchair,
    IconBriefcase,
} from "@tabler/icons-react";
import { InventoryItem } from "@turizi/shared";
import { BUSINESSES_PATH } from "@/lib/navigation";
import { useDeleteInventoryItem } from "@/hooks/api-hooks";

const ICON_MAP: Record<string, React.ComponentType<any>> = {
    IconBus, IconCar, IconSailboat, IconPlane, IconHelicopter, IconBike,
    IconBuilding, IconBed, IconHome, IconCompass, IconCube, IconLuggage,
    IconTools, IconArmchair, IconUser, IconUsers, IconUsersGroup,
    IconBabyCarriage, IconWheelchair, IconBriefcase
};

const getSelfAttrValue = (attrs: any[] | undefined, key: string, defaultValue: string) => {
    if (!attrs) return defaultValue;
    const attr = attrs.find(a => a.key === key);
    return attr ? attr.value : defaultValue;
};

const getItemIcon = (item: InventoryItem) => {
    const iconName = getSelfAttrValue(item.self_attrs, 'item_icon', 'IconCube');
    return ICON_MAP[iconName] || IconCube;
};

const getCapacityIcon = (item: InventoryItem) => {
    const iconName = getSelfAttrValue(item.self_attrs, 'capacity_icon', 'IconArmchair');
    return ICON_MAP[iconName] || IconArmchair;
};

interface InventoryItemNodeProps {
    item: InventoryItem;
    depth: number;
    expandedItems: Set<number>;
    toggleExpanded: (itemId: number) => void;
    getChildren: (parentId: number) => InventoryItem[];
    hasChildren: (itemId: number) => boolean;
    onSelect?: (item: InventoryItem) => void;
    onRemove?: (item: InventoryItem) => void;
    saving?: boolean;
    selected?: number[];
    loadingIds?: number[];
    onEditItem?: (item: InventoryItem) => void;
    onCreateChild?: (parent: InventoryItem) => void;
    draggingItemId: number | null;
    setDraggingItemId: (id: number | null) => void;
    onMoveItem: (itemId: number, newParentId: number | null) => void;
    items: InventoryItem[];
}

export default function InventoryItemNode({
    item,
    depth,
    expandedItems,
    toggleExpanded,
    getChildren,
    hasChildren,
    onSelect,
    onRemove,
    saving = false,
    selected = [],
    loadingIds = [],
    onEditItem,
    onCreateChild,
    draggingItemId,
    setDraggingItemId,
    onMoveItem,
    items,
}: InventoryItemNodeProps): JSX.Element {
    const { t } = useTranslation();
    const deleteInventoryItem = useDeleteInventoryItem();

    const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const isExpanded = expandedItems.has(item.id);
    const itemHasChildren = hasChildren(item.id);
    const isSelected = selected.includes(item.id);
    const isLoading = loadingIds.includes(item.id);
    const isDragging = draggingItemId === item.id;

    const isDescendantOf = (possibleParentId: number, possibleChildId: number): boolean => {
        let current = items.find(i => i.id === possibleChildId);
        while (current && current.parent_id) {
            if (current.parent_id === possibleParentId) return true;
            current = items.find(i => i.id === current.parent_id);
        }
        return false;
    };

    const handleDragStart = (e: React.DragEvent) => {
        setDraggingItemId(item.id);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", item.id.toString());
    };

    const handleDragEnd = () => {
        setDraggingItemId(null);
    };

    const handleDragOver = (e: React.DragEvent) => {
        if (draggingItemId !== null && draggingItemId !== item.id) {
            const isAlreadyParent = item.id === items.find(i => i.id === draggingItemId)?.parent_id;
            if (!isAlreadyParent && !isDescendantOf(draggingItemId, item.id)) {
                e.preventDefault();
                setIsDragOver(true);
            }
        }
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        if (draggingItemId !== null) {
            onMoveItem(draggingItemId, item.id);
        }
    };
    const handleDelete = async () => {
        try {
            await deleteInventoryItem.mutateAsync(item.id);
            setDeleteConfirmOpened(false);
        } catch (error) {
            console.error("Failed to delete inventory item:", error);
        }
    };

    const handleRowClick = (e: React.MouseEvent) => {
        if (onSelect || onRemove) {
            e.stopPropagation();
            if (isSelected) {
                onRemove?.(item);
            } else {
                onSelect?.(item);
            }
        }
    };

    return (
        <div className="w-full">
            {/* Tree Node Row */}
            <Menu
                disabled={!!onSelect || !!onRemove}
                position="bottom-start"
                shadow="md"
                withinPortal
            >
                <Menu.Target>
                    <div
                        draggable={!onSelect && !onRemove}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={handleRowClick}
                        className={`
                            group flex items-center justify-between
                            p-2 rounded-lg
                            cursor-pointer
                            hover:bg-slate-50 transition-all duration-155
                            ${isSelected ? "bg-green-50/60 border border-green-200/50" : ""}
                            ${isDragging ? "opacity-40 select-none bg-slate-100/50" : ""}
                            ${isDragOver
                                ? "border-2 border-dashed border-blue-400 bg-blue-50/30 scale-[1.01] shadow-sm"
                                : "border border-transparent"
                            }
                        `}
                    >
                        <div className="flex items-center gap-3 min-w-0">
                            {/* Expand/Collapse Chevron Indicator */}
                            {itemHasChildren ? (
                                <div
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleExpanded(item.id);
                                    }}
                                    className="flex items-center justify-center w-5 h-5 rounded hover:bg-slate-200/60 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer shrink-0"
                                >
                                    {isExpanded ? (
                                        <IconChevronDown size={14} stroke={2.5} />
                                    ) : (
                                        <IconChevronRight size={14} stroke={2.5} />
                                    )}
                                </div>
                            ) : (
                                <div className="w-5 h-5 shrink-0" />
                            )}

                            {/* Node Type Icon */}
                            <div className="shrink-0">
                                {itemHasChildren ? (
                                    isExpanded ? (
                                        <IconFolderOpen size={16} className="text-amber-500" />
                                    ) : (
                                        <IconFolder size={16} className="text-amber-400" />
                                    )
                                ) : (
                                    (() => {
                                        const ItemIcon = getItemIcon(item);
                                        return <ItemIcon size={16} className="text-slate-500" />;
                                    })()
                                )}
                            </div>

                            {/* Node Content */}
                            <div className="flex flex-col min-w-0">
                                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                    <span
                                        className={`text-sm truncate ${depth === 0 ? "font-bold text-slate-800" : "font-medium text-slate-700"
                                            }`}
                                    >
                                        {item.name}
                                    </span>

                                    {/* Linked Business Info */}
                                    {item.business_id && (
                                        <Link
                                            to={`${BUSINESSES_PATH}/${item.business_id}`}
                                            target="_blank"
                                            onClick={(e) => e.stopPropagation()}
                                            className="inline-flex items-center gap-1 bg-indigo-50/50 text-indigo-650 border border-indigo-100/40 rounded px-1.5 py-0.5 text-[9px] font-bold hover:bg-indigo-100 transition-colors"
                                        >
                                            <IconBuildingStore size={10} stroke={1.5} />
                                            <span>{item.business?.name || t("linked_business")}</span>
                                        </Link>
                                    )}

                                    {/* Capacity Tag */}
                                    {!itemHasChildren && item.capacity && (
                                        <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-750 border border-emerald-100/60 rounded px-1.5 py-0.5 text-[9px] font-extrabold uppercase">
                                            {(() => {
                                                const CapacityIcon = getCapacityIcon(item);
                                                return <CapacityIcon size={11} className="text-emerald-600 shrink-0" />;
                                            })()}
                                            <span>{t("capacity")}: {item.capacity}</span>
                                        </span>
                                    )}

                                    {/* Quantity Tag */}
                                    {!itemHasChildren && item.quantity && (
                                        <span className="inline-flex items-center bg-slate-50 text-slate-600 border border-slate-200/80 rounded px-1 py-0.25 text-[9px] font-extrabold uppercase">
                                            {t("qty")}: {item.quantity}
                                        </span>
                                    )}

                                    {/* Inline Notes Snippet */}
                                    {item.notes && (
                                        <span className="text-[10px] text-gray-400 max-w-[240px] truncate italic hidden sm:inline">
                                            &mdash; {item.notes}
                                        </span>
                                    )}
                                </div>
                                {/* <span className="text-[10px] text-slate-400 font-normal mt-0.5">
                                    {item.full_path}
                                </span> */}
                            </div>
                        </div>

                        {/* Right Side Actions Panel */}
                        <div className="flex items-center gap-1 shrink-0 ml-4">
                            {/* Select Node (for filter modals) */}
                            {(onSelect || onRemove) && (
                                <div onClick={(e) => e.stopPropagation()}>
                                    {isSelected ? (
                                        <Button
                                            size="xs"
                                            radius="md"
                                            variant="light"
                                            color="red"
                                            onClick={() => onRemove?.(item)}
                                            className="h-6 px-2 text-xs"
                                            loading={saving || isLoading}
                                        >
                                            {t("unselect")}
                                        </Button>
                                    ) : (
                                        <Button
                                            size="xs"
                                            variant="light"
                                            radius="md"
                                            color="blue"
                                            onClick={() => onSelect?.(item)}
                                            className="h-6 px-2 text-xs"
                                            loading={saving || isLoading}
                                        >
                                            {t("select")}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item
                        leftSection={<IconPlus size={15} stroke={2.5} />}
                        onClick={() => onCreateChild?.(item)}
                    >
                        {t("create_item_under_this_one")}
                    </Menu.Item>
                    <Menu.Item
                        leftSection={<IconEdit size={15} stroke={1.5} />}
                        onClick={() => onEditItem?.(item)}
                    >
                        {t("edit")}
                    </Menu.Item>
                    <Menu.Item
                        color="red"
                        leftSection={<IconTrash size={15} stroke={1.5} />}
                        onClick={() => setDeleteConfirmOpened(true)}
                    >
                        {t("delete")}
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>

            {/* Delete Confirmation Modal */}
            <Modal
                opened={deleteConfirmOpened}
                onClose={() => setDeleteConfirmOpened(false)}
                title={t("confirm_delete")}
                centered
                size="sm"
            >
                <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                        {t("confirm_delete_message")}
                    </p>
                    <div className="flex justify-end gap-2">
                        <Button
                            variant="subtle"
                            color="gray"
                            onClick={() => setDeleteConfirmOpened(false)}
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            color="red"
                            loading={deleteInventoryItem.isPending}
                            onClick={handleDelete}
                        >
                            {t("delete")}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Nested Child Nodes */}
            {isExpanded && itemHasChildren && (
                <div className="relative pl-6 border-l border-slate-100 ml-4.5 mt-1 space-y-1">
                    {getChildren(item.id).map((child) => (
                        <InventoryItemNode
                            key={child.id}
                            item={child}
                            depth={depth + 1}
                            expandedItems={expandedItems}
                            toggleExpanded={toggleExpanded}
                            getChildren={getChildren}
                            hasChildren={hasChildren}
                            onSelect={onSelect}
                            onRemove={onRemove}
                            selected={selected}
                            saving={saving}
                            loadingIds={loadingIds}
                            onEditItem={onEditItem}
                            onCreateChild={onCreateChild}
                            draggingItemId={draggingItemId}
                            setDraggingItemId={setDraggingItemId}
                            onMoveItem={onMoveItem}
                            items={items}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}